import React, { useEffect, useState } from 'react'
import { Row, Col, ProgressBar, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LOGO_URL } from 'Constants/Media'
import { LICENSE_PAYMENT } from 'Constants/Routes'
import { inject, observer } from 'mobx-react'
import apiService from 'Service'
import { formatCivilDate } from 'Utilities/dateTime'

const LicenceInfo = ({ companyStore, membershipsStore }) => {
    const { t } = useTranslation()
    const [licenseStatus, setLicenseStatus] = useState(null)

    useEffect(() => {
        if (companyStore.selectedCompany) {
            apiService.Licensing.status()
                .then(setLicenseStatus)
                .catch(() => setLicenseStatus(null))
        }
    }, [companyStore.selectedCompany])

    const remaining = licenseStatus?.RemainingDays ?? membershipsStore.currentMembership?.LicenseRemainingDays ?? 0
    const totalWindow = 30
    const progress = Math.min(100, Math.max(0, (remaining / totalWindow) * 100))
    const progressVariant = remaining <= 3 ? 'danger' : remaining <= 7 ? 'warning' : 'success'
    const expirationDate = licenseStatus?.ExpirationDate
        ? formatCivilDate(licenseStatus.ExpirationDate)
        : '-'

    return (
        <article className="dashboard-panel dashboard-license">
            <header className="dashboard-panel__header">
                <div>
                    <span className="dashboard-panel__eyebrow">
                        {t('dashboard_subscription', { defaultValue: 'Suscripción' })}
                    </span>
                    <h2>{t('dashboard_license_status', { defaultValue: 'Estado de licencia' })}</h2>
                </div>
                <img src={LOGO_URL} alt="SAM" className="dashboard-license__logo" />
            </header>

            <div className="dashboard-license__identity">
                <div>
                    <span>{t('membership')}</span>
                    <strong>{membershipsStore.currentMembership?.BusinessName || '-'}</strong>
                </div>
                <div>
                    <span>{t('company')}</span>
                    <strong>{companyStore.selectedCompany?.BusinessName || '-'}</strong>
                </div>
            </div>

            <Row className="dashboard-license__stats">
                <Col xs={6}>
                    <span>{t('licence_type')}</span>
                    <strong>{licenseStatus?.LicenseType || membershipsStore.currentMembership?.LicenseType || '-'}</strong>
                </Col>
                <Col xs={6}>
                    <span>{t('compies_count')}</span>
                    <strong>
                        {membershipsStore.currentMembership?.ActiveCompanies
                            ?? membershipsStore.currentMembership?.Companies?.length
                            ?? 0}
                        /
                        {membershipsStore.currentMembership?.MaxCompanies
                            ?? membershipsStore.currentMembership?.Companies?.length
                            ?? 0}
                    </strong>
                </Col>
            </Row>

            <div className="dashboard-license__progress">
                <div className="d-flex justify-content-between align-items-end">
                    <div>
                        <span>{t('days_left')}</span>
                        <strong>{remaining} {t('dashboard_days', { defaultValue: 'días' })}</strong>
                    </div>
                    <small>{t('license_expires_on')}: {expirationDate}</small>
                </div>
                <ProgressBar
                    now={progress}
                    variant={progressVariant}
                    aria-label={t('days_left')}
                    className="mt-2"
                />
            </div>

            <div className="dashboard-license__status">
                <span className={`dashboard-status-dot dashboard-status-dot--${progressVariant}`} aria-hidden="true" />
                <div>
                    <span>{t('status')}</span>
                    <strong>{licenseStatus?.Status || '-'}</strong>
                </div>
            </div>

            {licenseStatus?.HasPendingPayment && (
                <p className="dashboard-license__notice text-info">{t('license_payment_pending_validation')}</p>
            )}
            {licenseStatus?.CanAddCompany === false && (
                <p className="dashboard-license__notice text-warning">{t('company_limit_reached')}</p>
            )}

            <Button as={Link} to={LICENSE_PAYMENT} variant="primary" className="dashboard-license__cta" block>
                {t('report_license_payment')}
            </Button>
        </article>
    )
}

export default inject('companyStore', 'membershipsStore')(observer(LicenceInfo))
